import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
    BarChart3, Users, Clock, MousePointerClick, Globe, Monitor,
    Smartphone, Tablet, TrendingUp, RefreshCw, Eye, ArrowUpRight,
    Activity, Layers
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const StatCard = ({ icon: Icon, label, value, sub, color = "primary" }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-card border border-border rounded-xl p-5 flex flex-col gap-2"
    >
        <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">{label}</span>
            <Icon className={`w-5 h-5 text-${color}`} />
        </div>
        <div className="text-3xl font-bold text-foreground">{value}</div>
        {sub && <span className="text-xs text-muted-foreground">{sub}</span>}
    </motion.div>
);

const MiniBar = ({ data, maxVal }) => {
    const h = maxVal > 0 ? Math.max((data / maxVal) * 100, 4) : 0;
    return (
        <div className="flex flex-col items-center gap-1 flex-1 min-w-[8px]">
            <div className="w-full bg-muted rounded-t" style={{ height: '80px', position: 'relative' }}>
                <div
                    className="absolute bottom-0 w-full bg-primary/80 rounded-t transition-all duration-300"
                    style={{ height: `${h}%` }}
                />
            </div>
        </div>
    );
};

const DeviceIcon = ({ device }) => {
    if (device === "mobile") return <Smartphone className="w-4 h-4" />;
    if (device === "tablet") return <Tablet className="w-4 h-4" />;
    return <Monitor className="w-4 h-4" />;
};

const formatTime = (seconds) => {
    if (!seconds || seconds === 0) return "0s";
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const formatPageName = (path) => {
    if (path === "/" || path === "") return "Startseite";
    return path
        .replace(/^\//, "")
        .replace(/\//g, " / ")
        .replace(/-/g, " ")
        .replace(/\b\w/g, l => l.toUpperCase());
};

export const AnalyticsDashboard = () => {
    const [data, setData] = useState(null);
    const [realtime, setRealtime] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [period, setPeriod] = useState(30);
    const [autoRefresh, setAutoRefresh] = useState(true);
    const token = localStorage.getItem("token");

    const headers = { Authorization: `Bearer ${token}` };

    const fetchData = useCallback(async () => {
        try {
            setError(null);
            const [dashRes, rtRes] = await Promise.all([
                axios.get(`${BACKEND_URL}/api/analytics/dashboard?days=${period}`, { headers }),
                axios.get(`${BACKEND_URL}/api/analytics/realtime`, { headers })
            ]);
            setData(dashRes.data);
            setRealtime(rtRes.data);
        } catch (err) {
            console.error("Analytics fetch error:", err);
            if (err.response?.status === 401) {
                setError("Sitzung abgelaufen. Bitte erneut einloggen.");
            } else {
                setError("Analytics konnten nicht geladen werden.");
            }
        } finally {
            setLoading(false);
        }
    }, [period, token]);

    useEffect(() => { fetchData(); }, [fetchData]);

    useEffect(() => {
        if (!autoRefresh) return;
        const interval = setInterval(fetchData, 30000);
        return () => clearInterval(interval);
    }, [autoRefresh, fetchData]);

    if (loading) return (
        <div className="flex items-center justify-center h-96">
            <RefreshCw className="w-8 h-8 animate-spin text-primary" />
        </div>
    );

    if (error) return (
        <div className="flex flex-col items-center justify-center h-96 gap-4">
            <div className="bg-destructive/10 border border-destructive/30 rounded-xl px-6 py-4 text-center max-w-md">
                <p className="text-destructive font-medium">{error}</p>
                {error.includes("einloggen") && (
                    <button onClick={() => window.location.href = '/admin'} className="mt-3 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm hover:bg-primary/90">
                        Zum Login
                    </button>
                )}
            </div>
        </div>
    );

    const maxDayViews = data ? Math.max(...(data.visitors_by_day || []).map(d => d.page_views), 1) : 1;
    const totalDevices = (data?.devices || []).reduce((s, d) => s + d.count, 0) || 1;

    return (
        <div className="space-y-6" data-testid="analytics-dashboard">
            <div className="border border-border rounded-xl p-5 bg-muted/40">
                <div className="font-semibold text-foreground mb-1">Die Besucherstatistik ist umgezogen</div>
                <p className="text-sm text-muted-foreground">
                    Seit 23.09.2026 zählt teamWERK mit der gemeinsamen Statistik von Bürokult und teamWERK
                    (Verweildauer, Absprungraten, Klickwege, Ticketkäufe). Die Zahlen unten stammen aus der alten
                    Zählung und werden nicht mehr fortgeschrieben.
                </p>
                <a href="https://www.buerokult.de/admin/analytics" target="_blank" rel="noopener noreferrer"
                   className="inline-block mt-3 text-sm font-medium underline">
                    Zur neuen Besucherstatistik (Bürokult-Admin)
                </a>
            </div>
            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
                        <BarChart3 className="w-6 h-6 text-primary" /> Analytics
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        DSGVO-konform · Keine Cookies · Eigenes Tracking
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    {/* Period selector */}
                    <div className="flex bg-muted rounded-lg p-1">
                        {[7, 30, 90].map(d => (
                            <button
                                key={d}
                                onClick={() => { setPeriod(d); setLoading(true); }}
                                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                                    period === d ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                                }`}
                                data-testid={`period-${d}`}
                            >
                                {d}T
                            </button>
                        ))}
                    </div>
                    {/* Auto-refresh toggle */}
                    <button
                        onClick={() => setAutoRefresh(!autoRefresh)}
                        className={`p-2 rounded-lg transition-colors ${autoRefresh ? "bg-green-500/20 text-green-500" : "bg-muted text-muted-foreground"}`}
                        title={autoRefresh ? "Auto-Refresh aktiv (30s)" : "Auto-Refresh aus"}
                        data-testid="auto-refresh-toggle"
                    >
                        <Activity className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => { setLoading(true); fetchData(); }}
                        className="p-2 bg-muted rounded-lg hover:bg-muted/80 text-foreground"
                        data-testid="refresh-button"
                    >
                        <RefreshCw className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {/* Real-time banner */}
            {realtime && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="bg-green-500/10 border border-green-500/30 rounded-xl px-5 py-3 flex items-center justify-between"
                    data-testid="realtime-banner"
                >
                    <div className="flex items-center gap-3">
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                        <span className="text-sm font-medium text-green-600 dark:text-green-400">
                            {realtime.total_active} aktive Besucher jetzt
                        </span>
                    </div>
                    {realtime.active_pages?.length > 0 && (
                        <div className="flex gap-2 text-xs text-muted-foreground">
                            {realtime.active_pages.slice(0, 3).map((p, i) => (
                                <span key={i} className="bg-muted px-2 py-1 rounded">
                                    {formatPageName(p.page)} ({p.active_visitors})
                                </span>
                            ))}
                        </div>
                    )}
                </motion.div>
            )}

            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard icon={Eye} label="Seitenaufrufe" value={data?.total_page_views?.toLocaleString('de-DE') || "0"} sub={`Letzte ${period} Tage`} />
                <StatCard icon={Users} label="Eindeutige Besucher" value={data?.total_unique_visitors?.toLocaleString('de-DE') || "0"} sub="Anonymisierte IDs" />
                <StatCard icon={Clock} label="Ø Verweildauer" value={formatTime(data?.avg_time_on_page)} sub="Pro Seite" />
                <StatCard icon={MousePointerClick} label="Absprungrate" value={`${data?.bounce_rate || 0}%`} sub="Nur 1 Seite besucht" />
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Visitors Chart */}
                <div className="lg:col-span-2 bg-card border border-border rounded-xl p-5">
                    <h3 className="text-sm font-medium text-foreground mb-4 flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-primary" /> Besucher & Seitenaufrufe
                    </h3>
                    <div className="flex gap-[2px] items-end h-[120px]" data-testid="visitors-chart">
                        {(data?.visitors_by_day || []).map((d, i) => (
                            <div key={i} className="flex flex-col items-center gap-1 flex-1 min-w-0 group relative">
                                <div className="w-full bg-muted rounded-t relative" style={{ height: '100px' }}>
                                    <div
                                        className="absolute bottom-0 w-full bg-primary/70 rounded-t transition-all"
                                        style={{ height: `${Math.max((d.page_views / maxDayViews) * 100, 3)}%` }}
                                    />
                                    <div
                                        className="absolute bottom-0 w-full bg-primary rounded-t transition-all"
                                        style={{ height: `${Math.max((d.unique_visitors / maxDayViews) * 100, 2)}%` }}
                                    />
                                </div>
                                <div className="hidden group-hover:block absolute -top-16 left-1/2 -translate-x-1/2 bg-foreground text-background text-xs px-2 py-1 rounded whitespace-nowrap z-10">
                                    {d.date}: {d.page_views} Aufrufe, {d.unique_visitors} Besucher
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="flex justify-between mt-2 text-xs text-muted-foreground">
                        <span>{data?.visitors_by_day?.[0]?.date || ""}</span>
                        <div className="flex gap-4">
                            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-primary/70 rounded" /> Aufrufe</span>
                            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-primary rounded" /> Besucher</span>
                        </div>
                        <span>{data?.visitors_by_day?.[data.visitors_by_day.length - 1]?.date || ""}</span>
                    </div>
                </div>

                {/* Devices */}
                <div className="bg-card border border-border rounded-xl p-5">
                    <h3 className="text-sm font-medium text-foreground mb-4 flex items-center gap-2">
                        <Monitor className="w-4 h-4 text-primary" /> Geräte
                    </h3>
                    <div className="space-y-3" data-testid="devices-chart">
                        {(data?.devices || []).map((d, i) => (
                            <div key={i}>
                                <div className="flex items-center justify-between mb-1">
                                    <span className="text-sm flex items-center gap-2 text-foreground">
                                        <DeviceIcon device={d.device} />
                                        {d.device === "desktop" ? "Desktop" : d.device === "mobile" ? "Mobil" : "Tablet"}
                                    </span>
                                    <span className="text-sm text-muted-foreground">
                                        {Math.round((d.count / totalDevices) * 100)}%
                                    </span>
                                </div>
                                <div className="w-full h-2 bg-muted rounded-full">
                                    <div
                                        className="h-2 bg-primary rounded-full transition-all"
                                        style={{ width: `${(d.count / totalDevices) * 100}%` }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Second Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Top Pages */}
                <div className="bg-card border border-border rounded-xl p-5">
                    <h3 className="text-sm font-medium text-foreground mb-4 flex items-center gap-2">
                        <Layers className="w-4 h-4 text-primary" /> Top Seiten
                    </h3>
                    <div className="space-y-2" data-testid="top-pages">
                        {(data?.top_pages || []).slice(0, 10).map((p, i) => {
                            const timeData = data?.top_pages_time?.find(t => t.page === p.page);
                            return (
                                <div key={i} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                                    <div className="flex items-center gap-3">
                                        <span className="text-xs text-muted-foreground w-5">{i + 1}.</span>
                                        <span className="text-sm text-foreground">{formatPageName(p.page)}</span>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        {timeData && (
                                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                                                <Clock className="w-3 h-3" /> {formatTime(timeData.avg_time)}
                                            </span>
                                        )}
                                        <span className="text-sm font-medium text-foreground">{p.views}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Traffic Sources */}
                <div className="bg-card border border-border rounded-xl p-5">
                    <h3 className="text-sm font-medium text-foreground mb-4 flex items-center gap-2">
                        <Globe className="w-4 h-4 text-primary" /> Traffic-Quellen
                    </h3>
                    <div className="space-y-2" data-testid="traffic-sources">
                        {(data?.traffic_sources || []).map((s, i) => {
                            const total = (data?.traffic_sources || []).reduce((sum, x) => sum + x.count, 0) || 1;
                            return (
                                <div key={i} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                                    <div className="flex items-center gap-3">
                                        <ArrowUpRight className="w-4 h-4 text-muted-foreground" />
                                        <span className="text-sm text-foreground">{s.source}</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <div className="w-24 h-2 bg-muted rounded-full">
                                            <div className="h-2 bg-primary/60 rounded-full" style={{ width: `${(s.count / total) * 100}%` }} />
                                        </div>
                                        <span className="text-sm text-muted-foreground w-8 text-right">{s.count}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Third Row: Browsers + OS */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-card border border-border rounded-xl p-5">
                    <h3 className="text-sm font-medium text-foreground mb-4">Browser</h3>
                    <div className="space-y-2" data-testid="browsers-chart">
                        {(data?.browsers || []).map((b, i) => {
                            const total = (data?.browsers || []).reduce((s, x) => s + x.count, 0) || 1;
                            return (
                                <div key={i} className="flex items-center justify-between py-1.5">
                                    <span className="text-sm text-foreground">{b.browser}</span>
                                    <div className="flex items-center gap-2">
                                        <div className="w-20 h-1.5 bg-muted rounded-full">
                                            <div className="h-1.5 bg-primary/60 rounded-full" style={{ width: `${(b.count / total) * 100}%` }} />
                                        </div>
                                        <span className="text-xs text-muted-foreground w-8 text-right">{Math.round((b.count / total) * 100)}%</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <div className="bg-card border border-border rounded-xl p-5">
                    <h3 className="text-sm font-medium text-foreground mb-4">Betriebssystem</h3>
                    <div className="space-y-2" data-testid="os-chart">
                        {(data?.os_stats || []).map((o, i) => {
                            const total = (data?.os_stats || []).reduce((s, x) => s + x.count, 0) || 1;
                            return (
                                <div key={i} className="flex items-center justify-between py-1.5">
                                    <span className="text-sm text-foreground">{o.os}</span>
                                    <div className="flex items-center gap-2">
                                        <div className="w-20 h-1.5 bg-muted rounded-full">
                                            <div className="h-1.5 bg-primary/60 rounded-full" style={{ width: `${(o.count / total) * 100}%` }} />
                                        </div>
                                        <span className="text-xs text-muted-foreground w-8 text-right">{Math.round((o.count / total) * 100)}%</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Conversions */}
            {data?.conversions?.length > 0 && (
                <div className="bg-card border border-border rounded-xl p-5">
                    <h3 className="text-sm font-medium text-foreground mb-4 flex items-center gap-2">
                        <MousePointerClick className="w-4 h-4 text-primary" /> Conversions
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="conversions">
                        {data.conversions.map((c, i) => (
                            <div key={i} className="bg-muted/50 rounded-lg p-3 text-center">
                                <div className="text-2xl font-bold text-foreground">{c.count}</div>
                                <div className="text-xs text-muted-foreground mt-1">{c.type}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}